import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

interface Beam {
  /** Head position (the bright LED tip). */
  x: number;
  y: number;
  /** Travel direction in radians. */
  angle: number;
  /** Pixels travelled per frame (scaled by dt). */
  speed: number;
  /** Length of the glowing trail behind the head. */
  length: number;
  width: number;
  /** HSL hue for the beam colour. */
  hue: number;
  saturation: number;
  lightness: number;
  /** Per-beam base opacity for depth variation, 0 → 1. */
  alpha: number;
}

/**
 * Cool, technical palette (blues / indigo / slate) tuned to read clearly on a
 * LIGHT background — saturated mid/low lightness so the lines stay crisp and
 * "serious" rather than neon. Each entry is [hue, saturation, lightness].
 */
const PALETTE: ReadonlyArray<readonly [number, number, number]> = [
  [217, 91, 55], // blue (dominant)
  [221, 83, 53], // indigo
  [210, 90, 50], // azure
  [199, 89, 46], // cyan-blue accent
  [232, 55, 58], // restrained violet-blue
  [212, 28, 52], // cool slate (neutral)
];

const TWO_PI = Math.PI * 2;

function pick<T>(arr: ReadonlyArray<T>): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function spawnBeam(width: number, height: number): Beam {
  const [hue, saturation, lightness] = pick(PALETTE);
  // Bias angles toward the four diagonals so beams "percorrono lo schermo
  // da diverse angolazioni" instead of only horizontal/vertical sweeps.
  const baseAngles = [
    Math.PI / 5,
    Math.PI - Math.PI / 5,
    Math.PI + Math.PI / 5,
    TWO_PI - Math.PI / 5,
    Math.PI / 2.4,
    -Math.PI / 2.4,
  ];
  const angle = pick(baseAngles) + (Math.random() - 0.5) * 0.5;

  const length = 120 + Math.random() * 260;

  // Aim at a random point inside the viewport, then back the beam off along
  // the opposite of its travel direction by more than a full diagonal. This
  // guarantees it starts off-screen, crosses the whole viewport, and exits
  // the far side — never popping in or out mid-screen.
  const targetX = Math.random() * width;
  const targetY = Math.random() * height;
  const diagonal = Math.hypot(width, height);
  const backoff = diagonal + length + 80;
  const x = targetX - Math.cos(angle) * backoff;
  const y = targetY - Math.sin(angle) * backoff;

  return {
    x,
    y,
    angle,
    speed: 1.6 + Math.random() * 3.4,
    length,
    width: 1 + Math.random() * 1.8,
    hue,
    saturation,
    lightness,
    alpha: 0.55 + Math.random() * 0.45,
  };
}

/**
 * Full-bleed animated background of LED-style light beams streaking across the
 * screen from random diagonals. Canvas-based + additive blending for the glow,
 * capped device-pixel-ratio for performance, and disabled when the user
 * prefers reduced motion.
 */
export function LedBeamsBackground({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let width = 0;
    let height = 0;
    let dpr = 1;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    // Density scales with the viewport area, clamped to sensible bounds.
    const beamCount = Math.max(
      14,
      Math.min(40, Math.round((width * height) / 42000)),
    );
    const beams: Beam[] = Array.from({ length: beamCount }, () => {
      const b = spawnBeam(width, height);
      // Advance each beam a random distance along its path so the screen is
      // already populated on first paint (rather than waiting for them to
      // travel in from off-screen).
      const advance = Math.random() * (Math.hypot(width, height) + b.length);
      b.x += Math.cos(b.angle) * advance;
      b.y += Math.sin(b.angle) * advance;
      return b;
    });

    const drawBeam = (b: Beam) => {
      const alpha = b.alpha;
      const tailX = b.x - Math.cos(b.angle) * b.length;
      const tailY = b.y - Math.sin(b.angle) * b.length;

      const gradient = ctx.createLinearGradient(tailX, tailY, b.x, b.y);
      const color = (a: number) =>
        `hsla(${b.hue}, ${b.saturation}%, ${b.lightness}%, ${a})`;
      gradient.addColorStop(0, color(0));
      gradient.addColorStop(0.65, color(0.04 * alpha));
      gradient.addColorStop(1, color(0.9 * alpha));

      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = gradient;
      ctx.lineWidth = b.width;
      ctx.lineCap = "round";
      ctx.shadowBlur = 8;
      ctx.shadowColor = color(0.45 * alpha);
      ctx.stroke();

      // LED head — a crisp, saturated dot (darker, not lighter, so it stays
      // visible against the light background).
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.width * 1.3, 0, TWO_PI);
      ctx.fillStyle = `hsla(${b.hue}, ${b.saturation}%, ${Math.max(
        38,
        b.lightness - 6,
      )}%, ${alpha})`;
      ctx.shadowBlur = 10;
      ctx.fill();
    };

    let raf = 0;
    let last = performance.now();

    const frame = (now: number) => {
      const dt = Math.min(2.5, (now - last) / 16.67);
      last = now;

      // Fully clear each frame so beams leave NO residual trail in the
      // background — only their own crisp gradient body is drawn.
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < beams.length; i++) {
        const b = beams[i]!;
        b.x += Math.cos(b.angle) * b.speed * dt;
        b.y += Math.sin(b.angle) * b.speed * dt;

        // Respawn only once the whole beam (head + trail) has left the
        // viewport, so lines exit the screen instead of fading away.
        const offscreen =
          b.x < -b.length - 80 ||
          b.x > width + b.length + 80 ||
          b.y < -b.length - 80 ||
          b.y > height + b.length + 80;

        if (offscreen) {
          beams[i] = spawnBeam(width, height);
          continue;
        }
        drawBeam(b);
      }
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(frame);
    };

    if (reduceMotion) {
      // Paint a single static frame so there's still atmosphere (transparent
      // canvas → the light page background shows through).
      ctx.clearRect(0, 0, width, height);
      beams.forEach(drawBeam);
      ctx.shadowBlur = 0;
    } else {
      raf = requestAnimationFrame(frame);
    }

    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn("h-full w-full", className)}
    />
  );
}
